import {Fragment, useEffect, useRef, useState} from 'react';
import {motion} from 'framer-motion';
import {matchTokens} from '@cip/core/utils';
import styled from 'styled-components';

/**
 * A target line's visual treatment — mirrors the "Probability" (bold, white)
 * vs "for"/"AI" (light, gold) split in the settled hero title.
 */
export type LineVariant = 'default' | 'accent';

const LINE_VARIANT_STYLE: Record<
  LineVariant,
  {color: string; fontWeight: number}
> = {
  default: {color: 'inherit', fontWeight: 700},
  accent: {color: '#e8c4a0', fontWeight: 300},
};

/**
 * Duration of the per-character glide/fade tween. Mirrors the slide editor's
 * `MORPH_DURATION_SEC` (`src/apps/webapp/src/slides/shared/morphTiming.ts`);
 * redeclared here rather than imported since that module isn't exported
 * outside `@cip/webapp` and pulling in a cross-app export for one constant
 * isn't worth it.
 */
const MORPH_DURATION_SEC = 0.6;
/**
 * Crossfade from the animated glyph layer to the real settled JSX once the
 * glide finishes — absorbs any subpixel mismatch between measured and
 * rendered character positions.
 */
const BLEND_DURATION_SEC = 0.15;

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CharToken {
  key: string;
  rect: Rect;
  style: {color: string; fontWeight: number};
}

interface StyledLine {
  text: string;
  style: {color: string; fontWeight: number};
}

type Phase = 'pending' | 'animating' | 'blending' | 'settled';

const MorphWrapper = styled.div<{
  $lineCount: number;
  $measuredHeightPx?: number;
}>`
  position: relative;
  /* Reserved for the tallest state up front, so settling into it never
     shifts the rest of the hero's layout. The target block is absolutely
     positioned (see centeredBlockStyle's doc comment below), so it never
     contributes to this element's own height by itself — a line like
     "Artificial Intelligence" that wraps to two rows on a narrow viewport
     would otherwise overflow past a height reserved for one row per target
     line and overlap whatever renders after it. The measured height (the
     target's real, ResizeObserver-measured height, which already reflects
     however it actually wrapped) is used once available; the calc estimate
     only covers the brief window before that first measurement lands. */
  min-height: ${({$measuredHeightPx, $lineCount}) =>
    $measuredHeightPx === undefined
      ? `calc(1.1em * ${$lineCount})`
      : `${$measuredHeightPx}px`};
`;

/**
 * Both the source ("PAI") and target (settled title) blocks use this exact
 * positioning in every phase — vertically centered within `MorphWrapper` via
 * `top: 50%` + `translateY(-50%)`, never normal flow. Measuring the target
 * while hidden and displaying it once settled must be pixel-identical, or
 * the animation flies letters toward a position the real text doesn't end
 * up at; a single shared rule (rather than one for "hidden measurement" and
 * a different one — e.g. flex-centered flow — for "settled") makes that
 * true by construction instead of by coincidence.
 */
const centeredBlockStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: 0,
  right: 0,
  transform: 'translateY(-50%)',
};

/**
 * One word's characters, tagged with `data-*` attributes so
 * {@link measureCharTokens} can read them back without React refs. Wrapped
 * in its own `white-space: nowrap` box so the word can never break — without
 * this, splitting text into individual per-character spans removes the
 * browser's normal notion of "word" entirely, and it'll happily break a line
 * between *any* two adjacent letters (not just at real spaces).
 */
function Word({
  word,
  style,
}: {
  word: string;
  style: {color: string; fontWeight: number};
}) {
  return (
    <span style={{display: 'inline-block', whiteSpace: 'nowrap'}}>
      {word.split('').map((ch, charIndex) => (
        <span
          key={charIndex}
          data-ch={ch}
          data-color={style.color}
          data-font-weight={style.fontWeight}
          style={{display: 'inline-block'}}
        >
          {ch}
        </span>
      ))}
    </span>
  );
}

/**
 * Renders each line as a sequence of {@link Word}s separated by plain
 * (breakable) space spans — the only points a line is allowed to wrap. A
 * multi-word line wraps onto two rows when it doesn't fit and stays on one
 * row when it does, exactly like ordinary text (no fixed breakpoint).
 */
function CharLines({lines}: {lines: StyledLine[]}) {
  return (
    <>
      {lines.map((line, lineIndex) => {
        const words = line.text.split(' ');
        return (
          <div
            key={lineIndex}
            style={{color: line.style.color, fontWeight: line.style.fontWeight}}
          >
            {words.map((word, wordIndex) => (
              <Fragment key={wordIndex}>
                {wordIndex > 0 && (
                  <span
                    data-ch=" "
                    data-color={line.style.color}
                    data-font-weight={line.style.fontWeight}
                  >
                    {' '}
                  </span>
                )}
                <Word word={word} style={line.style} />
              </Fragment>
            ))}
          </div>
        );
      })}
    </>
  );
}

/** `el`'s viewport rect, converted to coordinates relative to `origin`. */
function toRelativeRect(el: Element, origin: DOMRect): Rect {
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left - origin.left,
    y: rect.top - origin.top,
    width: rect.width,
    height: rect.height,
  };
}

/**
 * Walks `container`'s rendered `CharLines` output and measures each
 * non-space character, in document (reading) order. Spaces are rendered (so
 * surrounding characters measure at their real positions) but excluded here
 * — they have no glyph to morph, and keying them would create ambiguous
 * many-to-one matches against every other space.
 */
function measureCharTokens(
  container: HTMLElement,
  origin: DOMRect,
): CharToken[] {
  const spans = container.querySelectorAll<HTMLSpanElement>('span[data-ch]');
  const tokens: CharToken[] = [];
  spans.forEach(span => {
    const ch = span.dataset.ch ?? '';
    if (ch === ' ') {
      return;
    }
    tokens.push({
      key: ch,
      rect: toRelativeRect(span, origin),
      style: {
        color: span.dataset.color ?? 'inherit',
        fontWeight: Number(span.dataset.fontWeight ?? '700'),
      },
    });
  });
  return tokens;
}

/**
 * The mid-morph glyph layer: matched characters glide from their source
 * position to their target position, removed characters fade out in place,
 * added ones fade in at their target position. Modeled on the slide
 * editor's `renderMorphLayer` (`GlyphMorphOverlay.tsx`), minus its
 * KaTeX-specific "visual atom" branch (radicals/fraction bars) — plain text
 * has no such concept, so every character takes the plain-glyph path.
 */
function renderGlyphLayer({
  matched,
  removed,
  added,
}: {
  matched: Array<[CharToken, CharToken]>;
  removed: CharToken[];
  added: CharToken[];
}) {
  // Base styling shared by every glyph span — position/size are supplied
  // per call site below (statically for removed/added, which don't resize;
  // animated for matched, which can — see the `width`/`height` note there).
  const glyphBaseStyle = (paint: CharToken) => ({
    position: 'absolute' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    whiteSpace: 'pre' as const,
    color: paint.style.color,
    fontWeight: paint.style.fontWeight,
  });
  return (
    <>
      {matched.map(([source, target], index) => {
        const dx = target.rect.x - source.rect.x;
        const dy = target.rect.y - source.rect.y;
        return (
          <motion.span
            key={`matched-${String(index)}`}
            // Animates the box's own width/height (not a uniform `scale`) —
            // a matched pair can change font-weight (e.g. "PAI"'s bold "A"
            // landing in a lighter-weight target line), which changes glyph
            // *width* independent of height. A single scale factor derived
            // from height alone would leave the box the wrong width, so the
            // glyph doesn't land exactly on the real settled letter.
            initial={{
              x: 0,
              y: 0,
              width: source.rect.width,
              height: source.rect.height,
            }}
            animate={{
              x: dx,
              y: dy,
              width: target.rect.width,
              height: target.rect.height,
            }}
            transition={{duration: MORPH_DURATION_SEC, ease: 'easeInOut'}}
            style={{
              ...glyphBaseStyle(target),
              left: source.rect.x,
              top: source.rect.y,
            }}
          >
            {target.key}
          </motion.span>
        );
      })}
      {removed.map((token, index) => (
        <motion.span
          key={`removed-${String(index)}`}
          initial={{opacity: 1, scale: 1}}
          animate={{opacity: 0, scale: 0.6}}
          transition={{duration: MORPH_DURATION_SEC, ease: 'easeInOut'}}
          style={{
            ...glyphBaseStyle(token),
            left: token.rect.x,
            top: token.rect.y,
            width: token.rect.width,
            height: token.rect.height,
          }}
        >
          {token.key}
        </motion.span>
      ))}
      {added.map((token, index) => (
        <motion.span
          key={`added-${String(index)}`}
          initial={{opacity: 0, scale: 0.6}}
          animate={{opacity: 1, scale: 1}}
          transition={{duration: MORPH_DURATION_SEC, ease: 'easeInOut'}}
          style={{
            ...glyphBaseStyle(token),
            left: token.rect.x,
            top: token.rect.y,
            width: token.rect.width,
            height: token.rect.height,
          }}
        >
          {token.key}
        </motion.span>
      ))}
    </>
  );
}

/** Props for {@link HeroTitleMorph}. */
export interface HeroTitleMorphProps {
  /** Lines shown before the morph, rendered with the `default` variant — e.g. `['PAI']`. */
  sourceLines: string[];
  /** Lines shown after the morph, each with its own color/weight treatment. */
  targetLines: Array<{text: string; variant: LineVariant}>;
  /** Delay, in ms, from mount before the morph begins — the "hold" that lets the source text register before it moves. Omitted defaults to `800`. */
  startDelayMs?: number;
  /** Called once when the morph reaches its settled state (also fired immediately under `prefers-reduced-motion`, which skips straight to settled). Omitted is a no-op. Use it to sequence a follow-on animation. */
  onSettled?: () => void;
}

/**
 * Renders `sourceLines`, holds, then glyph-morphs into `targetLines`:
 * matching characters glide to their new position (see {@link renderGlyphLayer}),
 * everything else fades. Must be rendered inside the same font-styled
 * ancestor (e.g. the caller's own `<h1>`) as the settled title it produces,
 * so measured character rects match what's actually displayed. Respects
 * `prefers-reduced-motion` by skipping straight to the settled target.
 *
 * Accessibility: every phase renders the title as per-character glyph spans,
 * which screen readers announce as fragments. Callers must wrap this in an
 * `aria-hidden` element and provide the settled title as plain text for
 * assistive tech (e.g. via `VisuallyHidden` — see `HeroSplash`).
 *
 * @param props - See {@link HeroTitleMorphProps}.
 * @return The rendered title, at whichever phase of the morph is current.
 * @throws Never.
 */
export function HeroTitleMorph({
  sourceLines,
  targetLines,
  startDelayMs = 800,
  onSettled,
}: HeroTitleMorphProps) {
  const [phase, setPhase] = useState<Phase>('pending');
  const [result, setResult] = useState<{
    matched: Array<[CharToken, CharToken]>;
    removed: CharToken[];
    added: CharToken[];
  }>();
  const [measuredHeightPx, setMeasuredHeightPx] = useState<number>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const [reducedMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  const sourceStyledLines: StyledLine[] = sourceLines.map(text => ({
    text,
    style: LINE_VARIANT_STYLE.default,
  }));
  const targetStyledLines: StyledLine[] = targetLines.map(
    ({text, variant}) => ({
      text,
      style: LINE_VARIANT_STYLE[variant],
    }),
  );

  useEffect(() => {
    const target = targetRef.current;
    if (target === null || typeof ResizeObserver === 'undefined') {
      return;
    }
    // The target block is always mounted (see `centeredBlockStyle`'s doc
    // comment), so its real rendered height — already reflecting however
    // many rows it actually wrapped into at the current viewport width — is
    // available from the start, independent of `phase`.
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry !== undefined) {
        setMeasuredHeightPx(entry.contentRect.height);
      }
    });
    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setPhase('settled');
      return;
    }
    const startTimeoutId = window.setTimeout(() => {
      const wrapper = wrapperRef.current;
      const source = sourceRef.current;
      const target = targetRef.current;
      if (wrapper === null || source === null || target === null) {
        setPhase('settled');
        return;
      }
      const origin = wrapper.getBoundingClientRect();
      const matchResult = matchTokens(
        measureCharTokens(source, origin),
        measureCharTokens(target, origin),
      );
      setResult(matchResult);
      setPhase('animating');
    }, startDelayMs);
    return () => {
      window.clearTimeout(startTimeoutId);
    };
    // Runs once on mount only — `reducedMotion` and `startDelayMs` are both
    // fixed for the component's lifetime (read once via lazy `useState`
    // initializers / a caller-fixed prop), so there's nothing to re-react to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== 'animating') {
      return;
    }
    const blendTimeoutId = window.setTimeout(() => {
      setPhase('blending');
    }, MORPH_DURATION_SEC * 1000);
    return () => {
      window.clearTimeout(blendTimeoutId);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'blending') {
      return;
    }
    const settleTimeoutId = window.setTimeout(() => {
      setPhase('settled');
    }, BLEND_DURATION_SEC * 1000);
    return () => {
      window.clearTimeout(settleTimeoutId);
    };
  }, [phase]);

  const settledFiredRef = useRef(false);
  useEffect(() => {
    if (phase !== 'settled' || settledFiredRef.current) {
      return;
    }
    settledFiredRef.current = true;
    onSettled?.();
  }, [phase, onSettled]);

  const targetSettled = phase === 'blending' || phase === 'settled';
  const lineCount = Math.max(sourceLines.length, targetLines.length);

  return (
    <MorphWrapper
      ref={wrapperRef}
      $lineCount={lineCount}
      $measuredHeightPx={measuredHeightPx}
    >
      {phase === 'pending' && (
        <div ref={sourceRef} style={centeredBlockStyle}>
          <CharLines lines={sourceStyledLines} />
        </div>
      )}
      <div
        ref={targetRef}
        style={
          targetSettled
            ? centeredBlockStyle
            : {
                ...centeredBlockStyle,
                visibility: 'hidden',
                pointerEvents: 'none',
              }
        }
        aria-hidden={targetSettled ? undefined : true}
      >
        <CharLines lines={targetStyledLines} />
      </div>
      {(phase === 'animating' || phase === 'blending') &&
        result !== undefined && (
          <motion.div
            animate={{opacity: phase === 'blending' ? 0 : 1}}
            transition={{duration: BLEND_DURATION_SEC, ease: 'linear'}}
            style={{position: 'absolute', inset: 0, pointerEvents: 'none'}}
          >
            {renderGlyphLayer(result)}
          </motion.div>
        )}
    </MorphWrapper>
  );
}
